// Sign-in modal, /login, /signup, /forgotpassword, /resetpassword
// (components/auth/JamAuth). See messages/README.md for how to add strings.
//
// auth.error.<scope>.<code> are the Firebase error texts from utils/tools.js
// (custom*ErrorMessages), one scope per map because the English differs
// between them. authErrorText() in JamAuth.js picks them by error.code.
export const auth = {
  en: {
    // Shared
    "auth.continueGoogle": "Continue with Google",
    "auth.orEmail": "or with email",
    "auth.email": "Email",
    "auth.emailInput": "Email Input",
    "auth.emailPlaceholder": "you@example.com",
    "auth.password": "Password",
    "auth.passwordInput": "Password Input",
    "auth.passwordPlaceholderNew": "6+ characters",
    "auth.signUp": "Sign up",
    "auth.logIn": "Log in",
    "auth.signIn": "Sign in",
    "auth.createAccount": "Create account",
    "auth.createYourAccount": "Create your account",
    "auth.forgotPassword": "Forgot password?",
    "auth.agree": "I agree to the",
    "auth.agreeJammable": "I agree to Jammable's",
    "auth.and": "and",
    "auth.terms": "Terms of Use",
    "auth.privacy": "Privacy Policy",
    "auth.agreeRequired":
      "Please agree to our Terms of Use and Privacy Policy.",
    "auth.welcomeBack": "Welcome back!",
    "auth.welcomeNew": "Welcome to Jammable!",
    "auth.registerError":
      "An error occurred during registration. Please try again.",
    "auth.backToLogin": "Back to log in",

    // Form validation
    "auth.invalidEmail": "Invalid email",
    "auth.emailRequired": "Email is required",
    "auth.passwordMin": "Password must be at least 6 characters",
    "auth.passwordRequired": "Password is required",

    // SignInModal
    "auth.modal.signInTitle": "Sign in to Jammable",
    "auth.modal.subtitle": "Make AI covers with 100,000+ voices.",
    "auth.modal.signUpButton": "Sign Up Button",
    "auth.modal.signInButton": "Sign In Button",
    "auth.modal.haveAccount": "Have an account?",
    "auth.modal.newHere": "New here?",
    "auth.modal.createAnAccount": "Create an account",

    // /login
    "auth.login.heading": "Welcome back",
    "auth.login.blurb":
      "Your covers, voices and collections are where you left them.",
    "auth.login.noAccount": "Don't have an account?",

    // /signup
    "auth.signup.heading": "Make any voice sing",
    "auth.signup.headingHighlight": "your audio",
    "auth.signup.blurb":
      "100,000+ voices, your covers saved in one place, and a studio for duets and videos.",
    "auth.signup.haveAccount": "Already have an account?",

    // /forgotpassword
    "auth.forgot.title": "Reset your password",
    "auth.forgot.blurb":
      "Enter your email and we'll send you a link to set a new one.",
    "auth.forgot.send": "Send reset link",
    "auth.forgot.sentToast": "Password reset email sent!",
    "auth.forgot.sent": "Sent. Check your inbox and spam folder.",
    "auth.forgot.google": "Signed up with Google?",
    "auth.forgot.googleAfter": "instead",

    // /resetpassword
    "auth.reset.title": "Set a new password",
    "auth.reset.blurb": "At least 6 characters. You'll log in with it next.",
    "auth.reset.newPassword": "New password",
    "auth.reset.submit": "Reset password",
    "auth.reset.success": "Password reset successful!",
    "auth.reset.expired": "Link expired?",
    "auth.reset.sendNew": "Send a new one",

    // Firebase errors: Google popup
    "auth.error.google.popup-closed-by-user":
      "The popup window was closed before completing the sign-in process. Please try again.",
    "auth.error.google.popup-blocked":
      "The popup window was blocked by the browser. Please enable popups and try again.",
    "auth.error.google.operation-not-supported-in-this-environment":
      "This sign-in method is not supported in your current environment.",
    "auth.error.google.network-request-failed":
      "A network error occurred. Please check your internet connection and try again.",
    "auth.error.google.cancelled-popup-request":
      "Multiple sign-in requests were detected. Please try again.",
    "auth.error.google.timeout":
      "The sign-in process took too long. Please check your internet connection and try again.",
    "auth.error.google.account-exists-with-different-credential":
      "An account with this email address already exists. Please sign in with the correct provider.",
    "auth.error.google.invalid-credential":
      "The provided sign-in credential is invalid or expired. Please try again.",
    "auth.error.google.user-disabled":
      "Your account has been disabled. Please contact support for assistance.",
    "auth.error.google.user-not-found":
      "No account found with this email address. Please try again or sign up for a new account.",
    "auth.error.google.wrong-password":
      "The password provided is incorrect. Please try again.",

    // Firebase errors: email sign-in
    "auth.error.signIn.invalid-email":
      "The email address you entered is invalid. Please check and try again.",
    "auth.error.signIn.user-disabled":
      "Your account has been disabled. Please contact support for assistance.",
    "auth.error.signIn.user-not-found":
      "No account found with this email address. Please try again or sign up for a new account.",
    "auth.error.signIn.wrong-password":
      "The password you entered is incorrect. Please try again.",
    "auth.error.signIn.too-many-requests":
      "Too many unsuccessful login attempts. Please try again later.",

    // Firebase errors: email sign-up
    "auth.error.signUp.email-already-in-use":
      "This email address is already in use by another account. Please use a different email.",
    "auth.error.signUp.invalid-email":
      "The email address you entered is invalid. Please check and try again.",
    "auth.error.signUp.operation-not-allowed":
      "Creating new accounts is currently not allowed. Please try again later.",
    "auth.error.signUp.weak-password":
      "The password must be at least 6 characters long. Please try a stronger password.",

    // Firebase errors: sending the reset email
    "auth.error.forgot.invalid-email": "Please enter a valid email address.",
    "auth.error.forgot.user-not-found": "No account found with this email.",
    "auth.error.forgot.missing-android-pkg-name":
      "An Android package name is required.",
    "auth.error.forgot.missing-continue-uri": "A continue URL is required.",
    "auth.error.forgot.missing-ios-bundle-id": "An iOS Bundle ID is required.",
    "auth.error.forgot.unauthorized-continue-uri":
      "The provided continue URL is not allowed.",
    "auth.error.forgot.operation-not-allowed":
      "Password reset is not enabled for this project.",

    // Firebase errors: setting the new password
    "auth.error.reset.invalid-action-code":
      "The action code is invalid. Please check the link and try again.",
    "auth.error.reset.expired-action-code":
      "The action code has expired. Please request a new password reset email.",
    "auth.error.reset.user-disabled":
      "This user account has been disabled. Please contact support.",
    "auth.error.reset.user-not-found": "No account found with this email.",
    "auth.error.reset.weak-password":
      "The password must be at least 6 characters.",
    "auth.error.reset.operation-not-allowed":
      "Password reset is not enabled for this project.",
  },
  es: {
    "auth.continueGoogle": "Continuar con Google",
    "auth.orEmail": "o con correo",
    "auth.email": "Correo electrónico",
    "auth.emailInput": "Correo electrónico",
    "auth.emailPlaceholder": "tu@ejemplo.com",
    "auth.password": "Contraseña",
    "auth.passwordInput": "Contraseña",
    "auth.passwordPlaceholderNew": "6 caracteres o más",
    "auth.signUp": "Regístrate",
    "auth.logIn": "Iniciar sesión",
    "auth.signIn": "Iniciar sesión",
    "auth.createAccount": "Crear cuenta",
    "auth.createYourAccount": "Crea tu cuenta",
    "auth.forgotPassword": "¿Olvidaste tu contraseña?",
    "auth.agree": "Acepto los",
    "auth.agreeJammable": "Acepto los",
    "auth.and": "y la",
    "auth.terms": "Términos de uso",
    "auth.privacy": "Política de privacidad",
    "auth.agreeRequired":
      "Acepta nuestros Términos de uso y Política de privacidad.",
    "auth.welcomeBack": "¡Hola de nuevo!",
    "auth.welcomeNew": "¡Te damos la bienvenida a Jammable!",
    "auth.registerError":
      "Ocurrió un error al crear tu cuenta. Inténtalo de nuevo.",
    "auth.backToLogin": "Volver a iniciar sesión",

    "auth.invalidEmail": "Correo no válido",
    "auth.emailRequired": "Ingresa tu correo",
    "auth.passwordMin": "La contraseña debe tener al menos 6 caracteres",
    "auth.passwordRequired": "Ingresa tu contraseña",

    "auth.modal.signInTitle": "Inicia sesión en Jammable",
    "auth.modal.subtitle": "Crea covers con IA con más de 100.000 voces.",
    "auth.modal.signUpButton": "Crear cuenta",
    "auth.modal.signInButton": "Iniciar sesión",
    "auth.modal.haveAccount": "¿Ya tienes cuenta?",
    "auth.modal.newHere": "¿Primera vez aquí?",
    "auth.modal.createAnAccount": "Crea una cuenta",

    "auth.login.heading": "Hola de nuevo",
    "auth.login.blurb":
      "Tus covers, voces y colecciones están donde los dejaste.",
    "auth.login.noAccount": "¿No tienes cuenta?",

    "auth.signup.heading": "Haz que cualquier voz cante",
    "auth.signup.headingHighlight": "tu audio",
    "auth.signup.blurb":
      "Más de 100.000 voces, tus covers guardados en un solo lugar y un estudio para duetos y videos.",
    "auth.signup.haveAccount": "¿Ya tienes cuenta?",

    "auth.forgot.title": "Restablece tu contraseña",
    "auth.forgot.blurb":
      "Ingresa tu correo y te enviaremos un enlace para crear una nueva.",
    "auth.forgot.send": "Enviar enlace",
    "auth.forgot.sentToast": "¡Te enviamos el correo para restablecerla!",
    "auth.forgot.sent":
      "Enviado. Revisa tu bandeja de entrada y la carpeta de spam.",
    "auth.forgot.google": "¿Te registraste con Google?",
    "auth.forgot.googleAfter": "",

    "auth.reset.title": "Crea una nueva contraseña",
    "auth.reset.blurb": "Al menos 6 caracteres. La usarás para iniciar sesión.",
    "auth.reset.newPassword": "Nueva contraseña",
    "auth.reset.submit": "Cambiar contraseña",
    "auth.reset.success": "¡Contraseña cambiada!",
    "auth.reset.expired": "¿El enlace expiró?",
    "auth.reset.sendNew": "Pide uno nuevo",

    "auth.error.google.popup-closed-by-user":
      "Cerraste la ventana antes de terminar de iniciar sesión. Inténtalo de nuevo.",
    "auth.error.google.popup-blocked":
      "El navegador bloqueó la ventana emergente. Permite las ventanas emergentes e inténtalo de nuevo.",
    "auth.error.google.operation-not-supported-in-this-environment":
      "Este método de inicio de sesión no funciona en tu navegador actual.",
    "auth.error.google.network-request-failed":
      "Hubo un error de red. Revisa tu conexión a internet e inténtalo de nuevo.",
    "auth.error.google.cancelled-popup-request":
      "Detectamos varios intentos de inicio de sesión. Inténtalo de nuevo.",
    "auth.error.google.timeout":
      "El inicio de sesión tardó demasiado. Revisa tu conexión a internet e inténtalo de nuevo.",
    "auth.error.google.account-exists-with-different-credential":
      "Ya existe una cuenta con este correo. Inicia sesión con el método que usaste al registrarte.",
    "auth.error.google.invalid-credential":
      "Las credenciales no son válidas o expiraron. Inténtalo de nuevo.",
    "auth.error.google.user-disabled":
      "Tu cuenta fue desactivada. Contacta a soporte para recibir ayuda.",
    "auth.error.google.user-not-found":
      "No hay ninguna cuenta con este correo. Inténtalo de nuevo o crea una cuenta.",
    "auth.error.google.wrong-password":
      "La contraseña es incorrecta. Inténtalo de nuevo.",

    "auth.error.signIn.invalid-email":
      "El correo que ingresaste no es válido. Revísalo e inténtalo de nuevo.",
    "auth.error.signIn.user-disabled":
      "Tu cuenta fue desactivada. Contacta a soporte para recibir ayuda.",
    "auth.error.signIn.user-not-found":
      "No hay ninguna cuenta con este correo. Inténtalo de nuevo o crea una cuenta.",
    "auth.error.signIn.wrong-password":
      "La contraseña que ingresaste es incorrecta. Inténtalo de nuevo.",
    "auth.error.signIn.too-many-requests":
      "Demasiados intentos fallidos. Inténtalo más tarde.",

    "auth.error.signUp.email-already-in-use":
      "Este correo ya está en uso en otra cuenta. Usa un correo diferente.",
    "auth.error.signUp.invalid-email":
      "El correo que ingresaste no es válido. Revísalo e inténtalo de nuevo.",
    "auth.error.signUp.operation-not-allowed":
      "Por ahora no se pueden crear cuentas nuevas. Inténtalo más tarde.",
    "auth.error.signUp.weak-password":
      "La contraseña debe tener al menos 6 caracteres. Elige una más segura.",

    "auth.error.forgot.invalid-email": "Ingresa un correo válido.",
    "auth.error.forgot.user-not-found":
      "No hay ninguna cuenta con este correo.",
    "auth.error.forgot.missing-android-pkg-name":
      "Se necesita un nombre de paquete de Android.",
    "auth.error.forgot.missing-continue-uri":
      "Se necesita una URL de continuación.",
    "auth.error.forgot.missing-ios-bundle-id":
      "Se necesita un Bundle ID de iOS.",
    "auth.error.forgot.unauthorized-continue-uri":
      "La URL de continuación no está permitida.",
    "auth.error.forgot.operation-not-allowed":
      "El restablecimiento de contraseña no está disponible.",

    "auth.error.reset.invalid-action-code":
      "El enlace no es válido. Revísalo e inténtalo de nuevo.",
    "auth.error.reset.expired-action-code":
      "El enlace expiró. Pide un nuevo correo para restablecer tu contraseña.",
    "auth.error.reset.user-disabled":
      "Esta cuenta fue desactivada. Contacta a soporte.",
    "auth.error.reset.user-not-found": "No hay ninguna cuenta con este correo.",
    "auth.error.reset.weak-password":
      "La contraseña debe tener al menos 6 caracteres.",
    "auth.error.reset.operation-not-allowed":
      "El restablecimiento de contraseña no está disponible.",
  },
  pt: {
    "auth.continueGoogle": "Continuar com o Google",
    "auth.orEmail": "ou com e-mail",
    "auth.email": "E-mail",
    "auth.emailInput": "E-mail",
    "auth.emailPlaceholder": "voce@exemplo.com",
    "auth.password": "Senha",
    "auth.passwordInput": "Senha",
    "auth.passwordPlaceholderNew": "6 ou mais caracteres",
    "auth.signUp": "Cadastre-se",
    "auth.logIn": "Entrar",
    "auth.signIn": "Entrar",
    "auth.createAccount": "Criar conta",
    "auth.createYourAccount": "Crie sua conta",
    "auth.forgotPassword": "Esqueceu a senha?",
    "auth.agree": "Concordo com os",
    "auth.agreeJammable": "Concordo com os",
    "auth.and": "e a",
    "auth.terms": "Termos de Uso",
    "auth.privacy": "Política de Privacidade",
    "auth.agreeRequired":
      "Aceite nossos Termos de Uso e a Política de Privacidade.",
    "auth.welcomeBack": "Que bom ter você de volta!",
    "auth.welcomeNew": "Boas-vindas ao Jammable!",
    "auth.registerError":
      "Ocorreu um erro ao criar sua conta. Tente novamente.",
    "auth.backToLogin": "Voltar para entrar",

    "auth.invalidEmail": "E-mail inválido",
    "auth.emailRequired": "Informe seu e-mail",
    "auth.passwordMin": "A senha precisa ter pelo menos 6 caracteres",
    "auth.passwordRequired": "Informe sua senha",

    "auth.modal.signInTitle": "Entre no Jammable",
    "auth.modal.subtitle": "Faça covers com IA com mais de 100.000 vozes.",
    "auth.modal.signUpButton": "Criar conta",
    "auth.modal.signInButton": "Entrar",
    "auth.modal.haveAccount": "Já tem uma conta?",
    "auth.modal.newHere": "Primeira vez aqui?",
    "auth.modal.createAnAccount": "Crie uma conta",

    "auth.login.heading": "Que bom ter você de volta",
    "auth.login.blurb":
      "Seus covers, vozes e coleções estão onde você os deixou.",
    "auth.login.noAccount": "Não tem uma conta?",

    "auth.signup.heading": "Faça qualquer voz cantar",
    "auth.signup.headingHighlight": "seu áudio",
    "auth.signup.blurb":
      "Mais de 100.000 vozes, seus covers salvos em um só lugar e um estúdio para duetos e vídeos.",
    "auth.signup.haveAccount": "Já tem uma conta?",

    "auth.forgot.title": "Redefina sua senha",
    "auth.forgot.blurb":
      "Informe seu e-mail e enviaremos um link para você criar uma nova.",
    "auth.forgot.send": "Enviar link",
    "auth.forgot.sentToast": "E-mail de redefinição enviado!",
    "auth.forgot.sent":
      "Enviado. Confira sua caixa de entrada e a pasta de spam.",
    "auth.forgot.google": "Se cadastrou com o Google?",
    "auth.forgot.googleAfter": "",

    "auth.reset.title": "Crie uma nova senha",
    "auth.reset.blurb": "Pelo menos 6 caracteres. Você vai usá-la para entrar.",
    "auth.reset.newPassword": "Nova senha",
    "auth.reset.submit": "Redefinir senha",
    "auth.reset.success": "Senha redefinida!",
    "auth.reset.expired": "O link expirou?",
    "auth.reset.sendNew": "Peça um novo",

    "auth.error.google.popup-closed-by-user":
      "A janela foi fechada antes de concluir o login. Tente novamente.",
    "auth.error.google.popup-blocked":
      "O navegador bloqueou a janela pop-up. Permita pop-ups e tente novamente.",
    "auth.error.google.operation-not-supported-in-this-environment":
      "Este método de login não funciona no seu navegador atual.",
    "auth.error.google.network-request-failed":
      "Ocorreu um erro de rede. Verifique sua conexão com a internet e tente novamente.",
    "auth.error.google.cancelled-popup-request":
      "Detectamos várias tentativas de login. Tente novamente.",
    "auth.error.google.timeout":
      "O login demorou demais. Verifique sua conexão com a internet e tente novamente.",
    "auth.error.google.account-exists-with-different-credential":
      "Já existe uma conta com este e-mail. Entre com o método que você usou no cadastro.",
    "auth.error.google.invalid-credential":
      "As credenciais são inválidas ou expiraram. Tente novamente.",
    "auth.error.google.user-disabled":
      "Sua conta foi desativada. Fale com o suporte para obter ajuda.",
    "auth.error.google.user-not-found":
      "Não encontramos uma conta com este e-mail. Tente novamente ou crie uma conta.",
    "auth.error.google.wrong-password":
      "A senha está incorreta. Tente novamente.",

    "auth.error.signIn.invalid-email":
      "O e-mail informado é inválido. Confira e tente novamente.",
    "auth.error.signIn.user-disabled":
      "Sua conta foi desativada. Fale com o suporte para obter ajuda.",
    "auth.error.signIn.user-not-found":
      "Não encontramos uma conta com este e-mail. Tente novamente ou crie uma conta.",
    "auth.error.signIn.wrong-password":
      "A senha informada está incorreta. Tente novamente.",
    "auth.error.signIn.too-many-requests":
      "Muitas tentativas sem sucesso. Tente novamente mais tarde.",

    "auth.error.signUp.email-already-in-use":
      "Este e-mail já está em uso por outra conta. Use um e-mail diferente.",
    "auth.error.signUp.invalid-email":
      "O e-mail informado é inválido. Confira e tente novamente.",
    "auth.error.signUp.operation-not-allowed":
      "No momento não é possível criar novas contas. Tente novamente mais tarde.",
    "auth.error.signUp.weak-password":
      "A senha precisa ter pelo menos 6 caracteres. Escolha uma senha mais forte.",

    "auth.error.forgot.invalid-email": "Informe um e-mail válido.",
    "auth.error.forgot.user-not-found":
      "Não encontramos uma conta com este e-mail.",
    "auth.error.forgot.missing-android-pkg-name":
      "É necessário um nome de pacote Android.",
    "auth.error.forgot.missing-continue-uri":
      "É necessária uma URL de continuação.",
    "auth.error.forgot.missing-ios-bundle-id":
      "É necessário um Bundle ID do iOS.",
    "auth.error.forgot.unauthorized-continue-uri":
      "A URL de continuação não é permitida.",
    "auth.error.forgot.operation-not-allowed":
      "A redefinição de senha não está disponível.",

    "auth.error.reset.invalid-action-code":
      "O link é inválido. Confira e tente novamente.",
    "auth.error.reset.expired-action-code":
      "O link expirou. Peça um novo e-mail de redefinição de senha.",
    "auth.error.reset.user-disabled":
      "Esta conta foi desativada. Fale com o suporte.",
    "auth.error.reset.user-not-found":
      "Não encontramos uma conta com este e-mail.",
    "auth.error.reset.weak-password":
      "A senha precisa ter pelo menos 6 caracteres.",
    "auth.error.reset.operation-not-allowed":
      "A redefinição de senha não está disponível.",
  },
};
